import { defineStore } from 'pinia';
import { updateAbility } from '@boqnet/permissions';
import { selectedOrgId } from '@/common/utils/role.utils';
import { withFlagGatedRolesRemoved } from '@/common/utils/role-flag-gate.utils';
import type { User as PermissionsUser } from '@boqnet/permissions';

export const usePermissionStore = defineStore('permissions', {
  state: () => ({
    user: null as PermissionsUser | null,
    isInitialized: false,
    /**
     * Whether LaunchDarkly has resolved for the session. Set once from App.vue when LaunchDarkly is
     * ready and either the signed-in user's identify has succeeded, failed or hit
     * `IDENTIFY_TIMEOUT_MS` (the last two leave the previous context's flags), or there is no user,
     * or flags are local. Logout resets every store, but with no user it is set again at once, so a later
     * sign-in in the same tab is not re-gated (D90). No project
     * role is flag-gated today, so this currently only marks the point at which the ability stops
     * being provisional.
     */
    areRoleFlagsResolved: false,
    /**
     * Whether the boot-time role refresh has settled. Until it has, the roles are the stored
     * session's, which can predate a role granted since. Deliberately not part of `isInitialized`,
     * which gates every route's denial (PE-1081).
     */
    areRolesRefreshed: false,
    activeProjectId: '',
    activeSiteId: '',
    /**
     * Incremented every time the CASL ability is rebuilt via refreshPermissions().
     * Reading this in a computed establishes a reactive dependency on the ability,
     * since the ability singleton itself is not tracked by Vue's reactivity system.
     */
    abilityVersion: 0,
  }),

  actions: {
    /**
     * Marks LaunchDarkly as resolved, which is what makes the ability authoritative. App.vue calls
     * it only with `true`, under the conditions on `areRoleFlagsResolved`.
     */
    setRoleFlagsResolved(value: boolean): void {
      if (this.areRoleFlagsResolved === value) return;

      this.areRoleFlagsResolved = value;
      this.refreshPermissions();
    },

    /**
    Called from `refreshPermissionsOnBoot` once the refresh has run, failed, or had no session.
    */
    setRolesRefreshed(): void {
      this.areRolesRefreshed = true;
    },

    /**
     * Updates the cached user and immediately rebuilds CASL abilities.
     * Call this from casl.utils initPermissionsWatcher or from auth store
     * actions whenever the logged-in user or selected org changes.
     */
    setUser(user: PermissionsUser | null): void {
      this.user = user;
      this.refreshPermissions();
    },

    /**
     * Rebuilds the CASL ability from the cached user and current context.
     * Prefer setUser() when the user itself has changed; call this directly
     * only when the project/site context changes without a user change.
     */
    refreshPermissions(): void {
      updateAbility(withFlagGatedRolesRemoved(this.user, {}), {
        activeOrgId: selectedOrgId.value ?? '',
        activeProjectId: this.activeProjectId,
        activeSiteId: this.activeSiteId,
      });

      // Context: `isInitialized` is what the router guard, App.vue and the Pusher role-change
      // revalidation all read before they are willing to DENY a route, so it has to mean "this
      // ability is authoritative" — not merely "a user is loaded". Until LaunchDarkly resolves,
      // any role the flag gate may withhold is on its default;
      // denying in that window strands a user on no-permissions, because the answer can change a
      // beat later but nothing navigates back.
      // That is the first-login denial a refresh appeared to fix (PE-588). App.vue renders the
      // layout once LD is ready, but the ability stays provisional until the user's identify
      // settles (at most IDENTIFY_TIMEOUT_MS), so on a cold load a route can render for about one
      // round-trip before a denial applies; App.vue re-checks the current route once it does.
      this.isInitialized = !!this.user && this.areRoleFlagsResolved;
      this.abilityVersion++;
    },

    setActiveProject(id: string): void {
      this.activeProjectId = id;
      this.refreshPermissions();
    },

    setActiveSite(id: string): void {
      this.activeSiteId = id;
      this.refreshPermissions();
    },

    resetContext(): void {
      this.activeProjectId = '';
      this.activeSiteId = '';
      this.isInitialized = false;

      this.refreshPermissions();
    },
  },
});
